// src/lib/opportunity/opportunityDocumentsClient.ts
//
// Browser-side helpers for the Opportunity Engine document workflow:
// sign → uploadToSignedUrl → commit (mirrors `opsPaperworkAttachmentsClient`)
// + listOpportunityDocuments for the Documents tab.
//
// `signed_url` on the list response is short-lived (90 s) — the tab
// re-pulls on demand rather than caching URLs in component state.

import { opportunityFetch } from "@/lib/opportunity/opportunityFetch";
import { requireSupabase } from "@/lib/supabase";
import type { OpportunityDocumentCategory } from "@/types/opportunity";

const SIGN_PATH = "/api/opportunity/document-sign";
const COMMIT_PATH = "/api/opportunity/document-commit";
const LIST_PATH = "/api/opportunity/document-list";

export type OpportunityDocument = {
  id: string;
  organization_id: string;
  opportunity_id: string;
  category: OpportunityDocumentCategory;
  document_type: string;
  version: number;
  is_active: boolean;
  display_name: string | null;
  storage_bucket: string;
  object_path: string;
  content_type: string;
  byte_size: number;
  uploaded_by_user_id: string | null;
  source_generated_output_id?: string | null;
  created_at: string;
  updated_at: string;
  signed_url?: string;
  signed_expires_at?: string;
};

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  document_id: string;
  content_type: string;
  category: OpportunityDocumentCategory;
  document_type: string;
};

type CommitOk = {
  ok: true;
  document: OpportunityDocument;
  archived_ids: string[];
};

export type OpportunityDocumentListResponse = {
  ok: true;
  documents: OpportunityDocument[];
};

function isSignOk(x: unknown): x is SignOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    o.ok === true &&
    typeof o.bucket === "string" &&
    typeof o.path === "string" &&
    typeof o.token === "string" &&
    typeof o.document_id === "string"
  );
}

function isCommitOk(x: unknown): x is CommitOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return o.ok === true && typeof o.document === "object" && o.document !== null;
}

export type UploadOpportunityDocumentResult =
  | { ok: true; document: OpportunityDocument; archived_ids: string[] }
  | { ok: false; error: string };

export async function uploadOpportunityDocument(args: {
  opportunityId: string;
  category: OpportunityDocumentCategory;
  documentType: string;
  file: File;
  displayName?: string;
  /** Optional link to the generated package this signed upload finalizes. */
  sourceGeneratedOutputId?: string | null;
}): Promise<UploadOpportunityDocumentResult> {
  const { opportunityId, category, documentType, file } = args;
  const sb = requireSupabase();
  const ct = (file.type || "application/octet-stream").toLowerCase();
  const displayName = (args.displayName ?? file.name).slice(0, 500);

  const sign = await opportunityFetch<unknown>(SIGN_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      opportunity_id: opportunityId,
      category,
      document_type: documentType,
      content_type: ct,
      byte_size: file.size,
      display_name: displayName,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  if (!isSignOk(sign.data)) {
    const err = (sign.data as { error?: unknown })?.error;
    return { ok: false, error: typeof err === "string" ? err : "Sign response invalid" };
  }
  const s = sign.data;

  const { error: upErr } = await sb.storage
    .from(s.bucket)
    .uploadToSignedUrl(s.path, s.token, file, {
      contentType: s.content_type,
      upsert: false,
    });
  if (upErr) return { ok: false, error: upErr.message };

  const commit = await opportunityFetch<unknown>(COMMIT_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      opportunity_id: opportunityId,
      document_id: s.document_id,
      object_path: s.path,
      category,
      document_type: documentType,
      content_type: s.content_type,
      byte_size: file.size,
      display_name: displayName,
      ...(args.sourceGeneratedOutputId
        ? { source_generated_output_id: args.sourceGeneratedOutputId }
        : {}),
    }),
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  if (!isCommitOk(commit.data)) {
    const err = (commit.data as { error?: unknown })?.error;
    return { ok: false, error: typeof err === "string" ? err : "Commit response invalid" };
  }
  return {
    ok: true,
    document: commit.data.document,
    archived_ids: commit.data.archived_ids ?? [],
  };
}

export function listOpportunityDocuments(args: { opportunityId: string; signMedia?: boolean }) {
  const q = new URLSearchParams();
  q.set("opportunity_id", args.opportunityId);
  if (args.signMedia) q.set("sign_media", "1");
  return opportunityFetch<OpportunityDocumentListResponse>(`${LIST_PATH}?${q.toString()}`, {
    method: "GET",
  });
}
